import React, { useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Phone, Mail, ShieldCheck, MessageCircle, FileText, Calendar, ExternalLink } from 'lucide-react';
const WHATSAPP_NUMBER = '14016967184';
const PDF_URL = 'https://gamma.app/docs/IUL-El-Plan-3-en-1-para-Proteger-a-tu-Familia-y-Jubilarte-con-Dig-hg4ru7rugrj9b1x';
const CALENDLY_URL = 'https://calendly.com/juanmanuelperezcamacho/30min';
function buildWhatsAppLink(name: string) {
  const message = encodeURIComponent(`Hola, soy ${name}. Me registré en el programa de protección familiar y ahorro. Quiero recibir más información.\n\n📄 Material: ${PDF_URL}\n📅 Agendar sesión: ${CALENDLY_URL}`);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
}
export function CtaSection() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && email.trim() && whatsapp.trim()) {
      setIsLoading(true);
      try {
        await fetch('https://services.leadconnectorhq.com/hooks/mm4XWU5oWtYb7RcmqRCk/webhook-trigger/c7aqJbtIOxIV9Yua7mos', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: name.trim(),
            email: email.trim(),
            phone: whatsapp.trim()
          }),
          mode: 'no-cors'
        });
      } catch {
        // Continue even if webhook fails
      }
      setIsLoading(false);
      window.open(buildWhatsAppLink(name.trim()), '_blank');
      setIsSubmitted(true);
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.4,
        ease: 'easeIn'
      }
    }
  };
  const stepsContainerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  const stepVariants = {
    hidden: {
      opacity: 0,
      x: -20
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  return <section id="reserva" className="relative min-h-[80vh] w-full flex items-center justify-center py-24 overflow-hidden" data-id="element-6">
      {/* Background Image */}
      <div className="absolute inset-0 z-0" style={{
      backgroundImage: 'url("https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=1920&q=80")',
      backgroundPosition: 'center',
      backgroundSize: 'cover'
    }} data-id="element-7">
        <div className="absolute inset-0 bg-magi-brown/60 mix-blend-multiply" data-id="element-8" />
        <div className="absolute inset-0 bg-black/40" data-id="element-9" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 md:px-12 text-center text-white max-w-4xl" data-id="element-10">
        <AnimatePresence mode="wait" data-id="element-11">
          {!isSubmitted ? <motion.div key="form" variants={containerVariants} initial="hidden" whileInView="visible" exit="exit" viewport={{
          once: true,
          margin: '-100px'
        }} className="bg-black/20 backdrop-blur-md p-8 md:p-12 lg:p-16 rounded-3xl border border-white/10 shadow-2xl" data-id="element-12">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight" data-id="element-13">
                No esperes a que sea demasiado tarde.
              </h2>

              <p className="text-lg md:text-xl font-light mb-10 text-gray-200 max-w-2xl mx-auto" data-id="element-14">
                Una sesión privada. Sin compromiso. Solo información que puede
                cambiar el futuro de tu familia.
              </p>

              <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-8 space-y-4" data-id="element-15">
                {/* Name */}
                <div className="relative" data-id="element-16">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-17">
                    <User className="h-5 w-5 text-white/60" data-id="element-18" />
                  </div>
                  <input type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Nombre completo" className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:outline-none focus:border-magi-beige focus:ring-1 focus:ring-magi-beige transition-all duration-300" data-id="element-19" />
                </div>

                {/* WhatsApp */}
                <div className="relative" data-id="element-20">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-21">
                    <Phone className="h-5 w-5 text-white/60" data-id="element-22" />
                  </div>
                  <input type="tel" required value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="Tu número de WhatsApp" className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:outline-none focus:border-magi-beige focus:ring-1 focus:ring-magi-beige transition-all duration-300" data-id="element-23" />
                </div>

                {/* Email */}
                <div className="relative" data-id="element-24">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-25">
                    <Mail className="h-5 w-5 text-white/60" data-id="element-26" />
                  </div>
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Tu correo electrónico" className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:outline-none focus:border-magi-beige focus:ring-1 focus:ring-magi-beige transition-all duration-300" data-id="element-27" />
                </div>

                <button type="submit" disabled={isLoading} className="w-full bg-magi-navy hover:bg-magi-navyHover disabled:opacity-70 disabled:cursor-not-allowed text-white px-8 py-4 rounded-xl text-lg font-medium transition-all duration-300 transform hover:scale-[1.02] disabled:hover:scale-100 shadow-xl mt-2" data-id="element-28">
                  {isLoading ? 'Enviando...' : 'Reserva tu sesión privada ahora'}
                </button>
              </form>

              <div className="flex flex-wrap items-center justify-center gap-2 text-sm md:text-base text-gray-300 font-light" data-id="element-29">
                <span data-id="element-30">Sin costo inicial</span>
                <span className="text-magi-beige hidden sm:inline" data-id="element-31">•</span>
                <span data-id="element-32">100% confidencial</span>
                <span className="text-magi-beige hidden sm:inline" data-id="element-33">•</span>
                <span data-id="element-34">En español</span>
              </div>
            </motion.div> : <motion.div key="confirmation" variants={containerVariants} initial="hidden" animate="visible" className="bg-black/30 backdrop-blur-md p-8 md:p-12 lg:p-16 rounded-3xl border border-white/10 shadow-2xl" data-id="element-35">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }} className="w-20 h-20 bg-magi-beige/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-36">
                <ShieldCheck className="w-10 h-10 text-magi-beige" data-id="element-37" />
              </motion.div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 leading-tight" data-id="element-38">
                ¡Gracias por dar el primer paso para proteger a tu familia
                {name ? `, ${name.split(' ')[0]}` : ''}!
              </h2>

              <p className="text-lg md:text-xl font-light mb-10 text-gray-200 max-w-2xl mx-auto" data-id="element-39">
                En unos momentos recibirás un mensaje directo en tu WhatsApp con
                información importante. Revisa tu teléfono y sigue los pasos.
              </p>

              <motion.div variants={stepsContainerVariants} initial="hidden" animate="visible" className="max-w-2xl mx-auto bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 text-left space-y-6 mb-8" data-id="element-40">
                <motion.div variants={stepVariants} className="flex items-start gap-4" data-id="element-41">
                  <div className="mt-1 bg-magi-navy/50 p-2 rounded-lg shrink-0" data-id="element-42">
                    <MessageCircle className="w-6 h-6 text-magi-beige" data-id="element-43" />
                  </div>
                  <p className="text-gray-200 font-light leading-relaxed" data-id="element-44">
                    <strong className="text-white font-medium block mb-1" data-id="element-45">
                      Paso 1
                    </strong>
                    Revisaremos tu caso y te enviaremos en breve el acceso a tu
                    sesión privada.
                  </p>
                </motion.div>

                <motion.div variants={stepVariants} className="flex items-start gap-4" data-id="element-46">
                  <div className="mt-1 bg-magi-navy/50 p-2 rounded-lg shrink-0" data-id="element-47">
                    <FileText className="w-6 h-6 text-magi-beige" data-id="element-48" />
                  </div>
                  <div className="text-gray-200 font-light leading-relaxed" data-id="element-49">
                    <strong className="text-white font-medium block mb-1" data-id="element-50">
                      Paso 2
                    </strong>
                    <p className="mb-2" data-id="element-51">
                      Revisa el material exclusivo para que llegues preparado a
                      tu sesión.
                    </p>
                    <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-magi-beige hover:text-white transition-colors text-sm font-medium" data-id="element-52">
                      <ExternalLink className="w-4 h-4" data-id="element-53" />
                      Ver material del programa
                    </a>
                  </div>
                </motion.div>

                <motion.div variants={stepVariants} className="flex items-start gap-4" data-id="element-54">
                  <div className="mt-1 bg-magi-navy/50 p-2 rounded-lg shrink-0" data-id="element-55">
                    <Calendar className="w-6 h-6 text-magi-beige" data-id="element-56" />
                  </div>
                  <div className="text-gray-200 font-light leading-relaxed" data-id="element-57">
                    <strong className="text-white font-medium block mb-1" data-id="element-58">
                      Paso 3
                    </strong>
                    <p className="mb-2" data-id="element-59">
                      ¿Quieres adelantar? Agenda tu sesión privada directamente.
                    </p>
                    <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-magi-beige hover:text-white transition-colors text-sm font-medium" data-id="element-60">
                      <ExternalLink className="w-4 h-4" data-id="element-61" />
                      Agendar sesión de 30 minutos
                    </a>
                  </div>
                </motion.div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1,
            duration: 0.5
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-62">
                <a href={buildWhatsAppLink(name.trim())} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-[1.02] shadow-lg" data-id="element-63">
                  <MessageCircle className="w-5 h-5" data-id="element-64" />
                  Enviar mensaje por WhatsApp
                </a>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-magi-navy hover:bg-magi-navyHover text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-[1.02] shadow-lg" data-id="element-65">
                  <Calendar className="w-5 h-5" data-id="element-66" />
                  Agendar sesión ahora
                </a>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}